import { useEffect, useState, useMemo } from "react";
import { api, formatINR, formatDate, formatApiError } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";
import JobLink from "@/components/JobLink";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Filter } from "lucide-react";
import { toast } from "sonner";
import { MACHINE_DEPTS } from "@/lib/employees";

const SHIFTS = ["A", "B", "C", "General"];

export default function Production() {
  // Operators, Machines and Wastage all read the same Printing-stage analytics the admin
  // dashboard uses (routes/production_stats.py) — one call, not three, and real data instead
  // of the separate, never-used Quick Entry log.
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const params = useMemo(() => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [dateFrom, dateTo]);

  const load = ({ quiet = false } = {}) => {
    if (!quiet) {
      setLoading(true);
      setLoadError(null);
    }
    return api.get("/dashboard/production", { params })
      .then(({ data }) => setStats(data))
      .catch((e) => {
        if (quiet) return;
        setStats(null);
        setLoadError(formatApiError(e));
        toast.error(`Failed to load performance data: ${formatApiError(e)}`);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params]);
  // Runs logged here, on a job page or by another user; machine / operator renames.
  const reload = useLiveRefresh(["production", "masters"], () => load({ quiet: true }));
  const operators = stats?.operators || [];
  const machines = stats?.machines || [];

  return (
    <div>
      <PageHeader
        subtitle="Shop Floor Analytics"
        title="Production Performance"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Filter size={14} className="text-neutral-500" />
            <Input data-testid="perf-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm w-36 md:w-40" />
            <span className="text-neutral-400 text-sm">→</span>
            <Input data-testid="perf-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-sm w-36 md:w-40" />
            {(dateFrom || dateTo) && (
              <Button variant="ghost" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); }} className="rounded-sm text-xs">Reset</Button>
            )}
          </div>
        }
      />
      <div className="p-8">
        <Tabs defaultValue="operators" className="w-full">
          <TabsList className="rounded-sm bg-white border border-neutral-200">
            <TabsTrigger data-testid="tab-operators" value="operators" className="rounded-sm">Operators</TabsTrigger>
            <TabsTrigger data-testid="tab-machines" value="machines" className="rounded-sm">Machines</TabsTrigger>
            <TabsTrigger data-testid="tab-wastage" value="wastage" className="rounded-sm">Wastage</TabsTrigger>
            <TabsTrigger data-testid="tab-logs" value="logs" className="rounded-sm">All Logs</TabsTrigger>
            <TabsTrigger data-testid="tab-quick" value="quick" className="rounded-sm">Quick Entry</TabsTrigger>
          </TabsList>

          <TabsContent value="operators" className="mt-4">
            <PerfTable rows={operators} testId="operator-perf-table" loading={loading} loadError={loadError} onRetry={load} />
          </TabsContent>

          <TabsContent value="machines" className="mt-4">
            <PerfTable rows={machines} testId="machine-perf-table" loading={loading} loadError={loadError} onRetry={load} />
          </TabsContent>

          <TabsContent value="wastage" className="mt-4">
            <WastageTab operators={operators} machines={machines} loading={loading} loadError={loadError} />
          </TabsContent>

          <TabsContent value="logs" className="mt-4">
            <LogsTab logs={stats?.logs || []} loading={loading} loadError={loadError} />
          </TabsContent>

          <TabsContent value="quick" className="mt-4">
            <QuickEntry onLogged={reload} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function PerfTable({ rows, testId, loading, loadError, onRetry }) {
  return (
    <div className="border border-neutral-200 bg-white overflow-x-auto">
      <table className="w-full text-sm" data-testid={testId}>
        <thead className="bg-neutral-50 label-caps">
          <tr>
            <th className="text-left px-4 py-2.5">Name</th>
            <th className="text-right px-4 py-2.5">Sessions</th>
            <th className="text-right px-4 py-2.5">Runtime hr</th>
            <th className="text-right px-4 py-2.5">Downtime hr</th>
            <th className="text-right px-4 py-2.5">Output (m)</th>
            <th className="text-right px-4 py-2.5">Waste (m)</th>
            <th className="text-right px-4 py-2.5">Waste %</th>
            <th className="text-right px-4 py-2.5">m/Hr</th>
            <th className="text-right px-4 py-2.5">Uptime %</th>
            <th className="text-right px-4 py-2.5">OEE %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} className="border-t border-neutral-100 data-row">
              <td className="px-4 py-2.5 font-medium">{r.name}</td>
              <td className="px-4 py-2.5 text-right font-mono-num">{r.sessions}</td>
              <td className="px-4 py-2.5 text-right font-mono-num">{r.hours}</td>
              <td className="px-4 py-2.5 text-right font-mono-num text-amber-700">{r.downtime_h}</td>
              <td className="px-4 py-2.5 text-right font-mono-num">{r.good_m?.toLocaleString("en-IN")}</td>
              <td className="px-4 py-2.5 text-right font-mono-num text-rose-700">{r.waste_m?.toLocaleString("en-IN")}</td>
              <td className={`px-4 py-2.5 text-right font-mono-num ${r.waste_pct > 5 ? "text-rose-700" : "text-neutral-900"}`}>{r.waste_pct ?? "—"}%</td>
              <td className="px-4 py-2.5 text-right font-mono-num font-semibold">{r.m_per_hour ?? "—"}</td>
              <td className={`px-4 py-2.5 text-right font-mono-num ${r.availability >= 85 ? "text-emerald-700" : r.availability >= 70 ? "text-amber-700" : "text-rose-700"}`}>
                {r.availability}%
              </td>
              <td className={`px-4 py-2.5 text-right font-mono-num font-semibold ${r.efficiency >= 65 ? "text-emerald-700" : r.efficiency >= 40 ? "text-amber-700" : "text-rose-700"}`}>
                {r.efficiency ?? "—"}%
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={10} className="px-4 py-10 text-center text-neutral-400">
              {loading ? (
                <span className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5 bg-[#FF5A00] rounded-full animate-pulse" />Loading production data…</span>
              ) : loadError ? (
                <span className="text-rose-700">Couldn't load data — {loadError}. {onRetry && <button onClick={onRetry} className="underline hover:text-rose-900">Retry</button>}</span>
              ) : (
                "No printing records in this range."
              )}
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function WastageTab({ operators, machines, loading, loadError }) {
  const [groupBy, setGroupBy] = useState("operator");
  const rows = [...(groupBy === "operator" ? operators : machines)].sort((a, b) => b.waste_m - a.waste_m);

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <span className="label-caps">Group by</span>
        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger data-testid="wastage-group-by" className="w-40 rounded-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="operator">Operator</SelectItem>
            <SelectItem value="machine">Machine</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="wastage-table">
          <thead className="bg-neutral-50 label-caps">
            <tr>
              <th className="text-left px-4 py-2.5">{groupBy === "operator" ? "Operator" : "Machine"}</th>
              <th className="text-right px-4 py-2.5">Sessions</th>
              <th className="text-right px-4 py-2.5">Output (m)</th>
              <th className="text-right px-4 py-2.5">Waste (m)</th>
              <th className="text-right px-4 py-2.5">Waste %</th>
              <th className="text-right px-4 py-2.5">Runtime hr</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} className="border-t border-neutral-100 data-row">
                <td className="px-4 py-2.5 font-medium">{r.name}</td>
                <td className="px-4 py-2.5 text-right font-mono-num">{r.sessions}</td>
                <td className="px-4 py-2.5 text-right font-mono-num">{r.good_m?.toLocaleString("en-IN")}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-rose-700 font-semibold">{r.waste_m?.toLocaleString("en-IN")}</td>
                <td className={`px-4 py-2.5 text-right font-mono-num ${r.waste_pct > 5 ? "text-rose-700 font-bold" : "text-neutral-900"}`}>{r.waste_pct ?? "—"}%</td>
                <td className="px-4 py-2.5 text-right font-mono-num">{r.hours}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-400">
                {loading ? "Loading…" : loadError ? `Couldn't load data — ${loadError}.` : "No wastage data in this range."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Reads the same `stats.logs` the parent already fetched from /dashboard/production — one
// row per print run, resolved operator/machine names and all. Options for the dropdowns come
// from the rows themselves (not the operators/machines masters), since a stage record's name
// may not resolve to a master record and the filter should only offer names actually present.
function LogsTab({ logs, loading, loadError }) {
  const [operator, setOperator] = useState("all");
  const [machine, setMachine] = useState("all");
  const [shift, setShift] = useState("all");

  const operatorOptions = useMemo(() => [...new Set(logs.map((l) => l.operator).filter(Boolean))].sort(), [logs]);
  const machineOptions = useMemo(() => [...new Set(logs.map((l) => l.machine).filter(Boolean))].sort(), [logs]);

  const rows = logs.filter((l) =>
    (operator === "all" || l.operator === operator) &&
    (machine === "all" || l.machine === machine) &&
    (shift === "all" || l.shift === shift)
  );

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-3 items-center">
        <Select value={operator} onValueChange={setOperator}>
          <SelectTrigger data-testid="logs-operator-filter" className="w-48 rounded-sm"><SelectValue placeholder="Operator" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All operators</SelectItem>
            {operatorOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={machine} onValueChange={setMachine}>
          <SelectTrigger data-testid="logs-machine-filter" className="w-48 rounded-sm"><SelectValue placeholder="Machine" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All machines</SelectItem>
            {machineOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={shift} onValueChange={setShift}>
          <SelectTrigger data-testid="logs-shift-filter" className="w-32 rounded-sm"><SelectValue placeholder="Shift" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All shifts</SelectItem>
            {["A", "B", "C"].map((s) => <SelectItem key={s} value={s}>Shift {s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto text-sm text-neutral-500">
          <span className="font-mono-num font-bold text-neutral-900">{rows.length.toLocaleString("en-IN")}</span> logs
          {logs.length >= 2000 && <span className="ml-1 text-neutral-400">(latest 2,000 in range)</span>}
        </div>
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="logs-table">
          <thead className="bg-neutral-50 label-caps">
            <tr>
              <th className="text-left px-4 py-2.5">Date</th>
              <th className="text-left px-4 py-2.5">Shift</th>
              <th className="text-left px-4 py-2.5">Job / MPWO</th>
              <th className="text-left px-4 py-2.5">Customer</th>
              <th className="text-left px-4 py-2.5">Operator</th>
              <th className="text-left px-4 py-2.5">Machine</th>
              <th className="text-right px-4 py-2.5">Hrs</th>
              <th className="text-right px-4 py-2.5">Output (m)</th>
              <th className="text-right px-4 py-2.5">Waste (m)</th>
              <th className="text-right px-4 py-2.5">Downtime hr</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((l, i) => (
              <tr key={`${l.job_id}-${i}`} className="border-t border-neutral-100 data-row">
                <td className="px-4 py-2.5 font-mono-num text-xs">{formatDate(l.date)}</td>
                <td className="px-4 py-2.5"><span className="text-xs px-1.5 py-0.5 bg-neutral-100 rounded-sm font-mono-num">{l.shift || "—"}</span></td>
                <td className="px-4 py-2.5 font-mono-num text-xs">
                  <JobLink id={l.job_id} number={l.bpr} tab="production" className="text-neutral-900" />
                </td>
                <td className="px-4 py-2.5 text-xs">{l.customer_name}</td>
                <td className="px-4 py-2.5">{l.operator}</td>
                <td className="px-4 py-2.5">{l.machine}</td>
                <td className="px-4 py-2.5 text-right font-mono-num">{l.hours}</td>
                <td className="px-4 py-2.5 text-right font-mono-num">{l.good_m?.toLocaleString("en-IN")}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-rose-700">{l.waste_m?.toLocaleString("en-IN")}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-amber-700">{l.downtime_h || "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-neutral-400">
                {loading ? "Loading…" : loadError ? `Couldn't load data — ${loadError}.` : "No printing records match these filters."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function QuickEntry({ onLogged }) {
  const [jobs, setJobs] = useState([]);
  const [operators, setOperators] = useState([]);
  const [machines, setMachines] = useState([]);
  const [jobQuery, setJobQuery] = useState("");
  const [f, setF] = useState({
    job_id: "", operator_id: "", machine_id: "", shift: "A",
    log_date: new Date().toISOString().slice(0, 10),
    hours_run: 0, output_qty: 0, waste_qty: 0,
    setup_waste: "", lsd_meters: "",
    downtime_mins: 0, downtime_reason: "", notes: "",
  });

  useEffect(() => {
    api.get("/operators").then(({ data }) => setOperators(data.filter(o => o.status === "active" && MACHINE_DEPTS.includes(o.department)))).catch(() => {});
    api.get("/machines").then(({ data }) => setMachines(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (jobQuery.length >= 2) {
        api.get("/jobs", { params: { q: jobQuery, limit: 30 } }).then(({ data }) => setJobs(data)).catch(() => setJobs([]));
      } else {
        api.get("/jobs", { params: { limit: 30 } }).then(({ data }) => setJobs(data)).catch(() => {});
      }
    }, 250);
    return () => clearTimeout(t);
  }, [jobQuery]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.job_id || !f.operator_id || !f.machine_id) {
      toast.error("Job, operator and machine are required"); return;
    }
    try {
      await api.post("/production/log", {
        job_id: f.job_id, operator_id: f.operator_id, machine_id: f.machine_id,
        shift: f.shift, log_date: f.log_date,
        hours_run: Number(f.hours_run),
        output_qty: Number(f.output_qty),
        waste_qty: Number(f.waste_qty),
        setup_waste: f.setup_waste === "" ? null : Number(f.setup_waste),
        lsd_meters: f.lsd_meters === "" ? null : Number(f.lsd_meters),
        downtime_mins: Number(f.downtime_mins),
        downtime_reason: f.downtime_reason,
        notes: f.notes,
      });
      toast.success("Production log saved");
      setF({ ...f, output_qty: 0, waste_qty: 0, setup_waste: "", lsd_meters: "", hours_run: 0, downtime_mins: 0, downtime_reason: "", notes: "" });
      onLogged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="border border-neutral-200 bg-white p-6 max-w-4xl">
      <div className="label-caps mb-4">Log Production Run</div>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="label-caps block mb-1.5">Job</label>
          <Input data-testid="quick-job-search" placeholder="Search by MPWO / customer / product…" value={jobQuery} onChange={(e) => setJobQuery(e.target.value)} className="rounded-sm mb-2" />
          <Select value={f.job_id} onValueChange={(v) => setF({ ...f, job_id: v })}>
            <SelectTrigger data-testid="quick-job-select" className="rounded-sm"><SelectValue placeholder="Select job…" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {jobs.map((j) => (
                <SelectItem key={j.id} value={j.id}>
                  {j.mpwo_number || j.job_number} · {j.customer_name} · {j.product_name?.slice(0, 40)}
                </SelectItem>
              ))}
              {jobs.length === 0 && <SelectItem value="none" disabled>No jobs</SelectItem>}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="label-caps block mb-1.5">Operator</label>
          <Select value={f.operator_id} onValueChange={(v) => setF({ ...f, operator_id: v })}>
            <SelectTrigger data-testid="quick-operator" className="rounded-sm"><SelectValue placeholder="Select operator…" /></SelectTrigger>
            <SelectContent>
              {operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.code} · {o.name}</SelectItem>)}
              {operators.length === 0 && <SelectItem value="none" disabled>No operators — add via Operators page</SelectItem>}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="label-caps block mb-1.5">Machine</label>
          <Select value={f.machine_id} onValueChange={(v) => setF({ ...f, machine_id: v })}>
            <SelectTrigger data-testid="quick-machine" className="rounded-sm"><SelectValue placeholder="Select machine…" /></SelectTrigger>
            <SelectContent>
              {machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.code} · {m.name}</SelectItem>)}
              {machines.length === 0 && <SelectItem value="none" disabled>No machines — add via Machines page</SelectItem>}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="label-caps block mb-1.5">Shift</label>
          <Select value={f.shift} onValueChange={(v) => setF({ ...f, shift: v })}>
            <SelectTrigger data-testid="quick-shift" className="rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{SHIFTS.map((s) => <SelectItem key={s} value={s}>Shift {s}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div>
          <label className="label-caps block mb-1.5">Date</label>
          <Input data-testid="quick-date" type="date" value={f.log_date} onChange={(e) => setF({ ...f, log_date: e.target.value })} className="rounded-sm" required />
        </div>

        <div>
          <label className="label-caps block mb-1.5">Hours Run</label>
          <Input data-testid="quick-hours" type="number" step="0.25" value={f.hours_run} onChange={(e) => setF({ ...f, hours_run: e.target.value })} className="rounded-sm" required />
        </div>

        <div>
          <label className="label-caps block mb-1.5">Downtime (mins)</label>
          <Input data-testid="quick-downtime" type="number" step="1" value={f.downtime_mins} onChange={(e) => setF({ ...f, downtime_mins: e.target.value })} className="rounded-sm" />
        </div>

        <div>
          <label className="label-caps block mb-1.5">Good Output Qty</label>
          <Input data-testid="quick-output" type="number" value={f.output_qty} onChange={(e) => setF({ ...f, output_qty: e.target.value })} className="rounded-sm" />
        </div>

        <div>
          <label className="label-caps block mb-1.5">Wastage / Rejection Qty</label>
          <Input data-testid="quick-waste" type="number" value={f.waste_qty} onChange={(e) => setF({ ...f, waste_qty: e.target.value })} className="rounded-sm" />
        </div>

        <div className="md:col-span-2 pt-2 border-t border-neutral-100">
          <div className="label-caps text-[10px] text-neutral-500 mb-2">Wastage breakdown (meters) — optional · feeds Operator Output report</div>
        </div>

        <div>
          <label className="label-caps block mb-1.5">Setup Waste (m)</label>
          <Input data-testid="quick-setup-waste" type="number" step="0.01" placeholder="e.g. 800" value={f.setup_waste} onChange={(e) => setF({ ...f, setup_waste: e.target.value })} className="rounded-sm" />
        </div>

        <div>
          <label className="label-caps block mb-1.5">LSD Meters (m)</label>
          <Input data-testid="quick-lsd-meters" type="number" step="0.01" placeholder="e.g. 150" value={f.lsd_meters} onChange={(e) => setF({ ...f, lsd_meters: e.target.value })} className="rounded-sm" />
        </div>

        <div className="md:col-span-2">
          <label className="label-caps block mb-1.5">Downtime Reason</label>
          <Input data-testid="quick-downtime-reason" placeholder="e.g. Plate change, Material break, Power cut" value={f.downtime_reason} onChange={(e) => setF({ ...f, downtime_reason: e.target.value })} className="rounded-sm" />
        </div>

        <div className="md:col-span-2 flex justify-end">
          <Button data-testid="quick-submit" type="submit" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
            <Plus size={14} className="mr-1.5" /> Save Log
          </Button>
        </div>
      </form>
    </div>
  );
}
